import { useState, useEffect } from "react";
import RichText from "@/components/RichText";
import { Plus, Trash2 } from "lucide-react";

// Reusable primitives ----------------------------------------------------------
export function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-[0.15em] text-[#5C6661] mb-2">{label}</div>
      {children}
      {hint && <div className="mt-1 text-xs text-[#8A8F8B]">{hint}</div>}
    </label>
  );
}

export function TextInput({ value, onChange, testId, ...rest }) {
  return (
    <input
      data-testid={testId}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-2 rounded-lg border border-[#E5E0D8] bg-white focus:outline-none focus:ring-2 focus:ring-[#D4A373]"
      {...rest}
    />
  );
}

export function TextArea({ value, onChange, testId, rows = 3, ...rest }) {
  return (
    <textarea
      data-testid={testId}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      className="w-full px-4 py-2 rounded-lg border border-[#E5E0D8] bg-white focus:outline-none focus:ring-2 focus:ring-[#D4A373]"
      {...rest}
    />
  );
}

export function Toggle({ value, onChange, testId, label }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => onChange(!value)}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm border transition-colors ${value ? "bg-[#2C4C3B] text-white border-[#2C4C3B]" : "bg-white text-[#1C201E] border-[#E5E0D8]"}`}
    >
      <span className={`w-8 h-4 rounded-full relative ${value ? "bg-white/30" : "bg-[#E5E0D8]"}`}>
        <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${value ? "left-4" : "left-0.5"}`} />
      </span>
      {label}
    </button>
  );
}

function ArrayEditor({ items, onChange, renderItem, factory, addLabel, testIdBase }) {
  const set = (i, next) => onChange(items.map((it, idx) => (idx === i ? next : it)));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...(items || []), factory()]);
  return (
    <div className="space-y-3">
      {(items || []).map((item, i) => (
        <div key={i} data-testid={`${testIdBase}-item-${i}`} className="p-4 rounded-xl border border-[#E5E0D8] bg-[#FAF9F6]">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-[0.15em] text-[#5C6661]">Item {i + 1}</div>
            <button type="button" data-testid={`${testIdBase}-remove-${i}`} onClick={() => remove(i)} className="p-1.5 text-red-600 hover:bg-red-50 rounded">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          {renderItem(item, (next) => set(i, next), i)}
        </div>
      ))}
      <button type="button" data-testid={`${testIdBase}-add`} onClick={add} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-dashed border-[#2C4C3B]/50 text-[#2C4C3B] text-sm hover:bg-[#2C4C3B] hover:text-white">
        <Plus className="w-4 h-4" /> {addLabel}
      </button>
    </div>
  );
}

// Section forms ---------------------------------------------------------------
function HeroForm({ data, set }) {
  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Pastor's Name"><TextInput testId="hero-pastor-name" value={data.pastor_name} onChange={(v) => set({ ...data, pastor_name: v })} /></Field>
        <Field label="Pastor Photo URL"><TextInput testId="hero-pastor-photo" value={data.pastor_photo} onChange={(v) => set({ ...data, pastor_photo: v })} /></Field>
      </div>
      <Field label="Title"><TextInput testId="hero-title" value={data.title} onChange={(v) => set({ ...data, title: v })} /></Field>
      <Field label="Subtitle / Eyebrow"><TextInput testId="hero-subtitle" value={data.subtitle} onChange={(v) => set({ ...data, subtitle: v })} /></Field>
      <Field label="Pastor's Message" hint="Rich text: bold, italic, lists, quotes.">
        <RichText testId="hero-message" value={data.message} onChange={(v) => set({ ...data, message: v })} />
      </Field>
      <Field label="Verse"><TextArea testId="hero-verse" value={data.verse} onChange={(v) => set({ ...data, verse: v })} rows={2} /></Field>
    </div>
  );
}

function ScheduleForm({ data, set }) {
  return (
    <div className="space-y-4">
      <Field label="Heading"><TextInput testId="sched-heading" value={data.heading} onChange={(v) => set({ ...data, heading: v })} /></Field>
      <Field label="Subtitle"><TextInput testId="sched-subtitle" value={data.subtitle} onChange={(v) => set({ ...data, subtitle: v })} /></Field>
      <Field label="Services">
        <ArrayEditor
          items={data.services}
          onChange={(services) => set({ ...data, services })}
          factory={() => ({ day: "", time: "", name: "" })}
          addLabel="Add service"
          testIdBase="sched-service"
          renderItem={(item, upd) => (
            <div className="grid md:grid-cols-3 gap-3">
              <TextInput testId="sched-item-day" value={item.day} placeholder="Day (e.g. Sunday)" onChange={(v) => upd({ ...item, day: v })} />
              <TextInput testId="sched-item-time" value={item.time} placeholder="Time (e.g. 9:00 AM)" onChange={(v) => upd({ ...item, time: v })} />
              <TextInput testId="sched-item-name" value={item.name} placeholder="Service Name" onChange={(v) => upd({ ...item, name: v })} />
            </div>
          )}
        />
      </Field>
    </div>
  );
}

function LocationForm({ data, set }) {
  return (
    <div className="space-y-4">
      <Field label="Heading"><TextInput testId="loc-heading" value={data.heading} onChange={(v) => set({ ...data, heading: v })} /></Field>
      <Field label="Address"><TextArea testId="loc-address" value={data.address} onChange={(v) => set({ ...data, address: v })} rows={2} /></Field>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Phone"><TextInput testId="loc-phone" value={data.phone} onChange={(v) => set({ ...data, phone: v })} /></Field>
        <Field label="Email"><TextInput testId="loc-email" value={data.email} onChange={(v) => set({ ...data, email: v })} /></Field>
      </div>
      <Field label="Directions URL"><TextInput testId="loc-directions" value={data.directions_url} onChange={(v) => set({ ...data, directions_url: v })} /></Field>
      <Field label="Map Embed URL"><TextInput testId="loc-map" value={data.map_embed} onChange={(v) => set({ ...data, map_embed: v })} /></Field>
    </div>
  );
}

function MinistriesForm({ data, set }) {
  return (
    <div className="space-y-4">
      <Field label="Heading"><TextInput testId="min-heading" value={data.heading} onChange={(v) => set({ ...data, heading: v })} /></Field>
      <Field label="Subtitle"><TextInput testId="min-subtitle" value={data.subtitle} onChange={(v) => set({ ...data, subtitle: v })} /></Field>
      <Field label="Ministries">
        <ArrayEditor
          items={data.items}
          onChange={(items) => set({ ...data, items })}
          factory={() => ({ title: "", description: "" })}
          addLabel="Add ministry"
          testIdBase="min"
          renderItem={(item, upd) => (
            <div className="space-y-3">
              <TextInput testId="min-item-title" value={item.title} placeholder="Ministry title" onChange={(v) => upd({ ...item, title: v })} />
              <TextArea testId="min-item-desc" value={item.description} placeholder="Short description" onChange={(v) => upd({ ...item, description: v })} rows={2} />
            </div>
          )}
        />
      </Field>
    </div>
  );
}

function BeliefsForm({ data, set }) {
  return (
    <div className="space-y-4">
      <Field label="Heading"><TextInput testId="bel-heading" value={data.heading} onChange={(v) => set({ ...data, heading: v })} /></Field>
      <Field label="Subtitle"><TextInput testId="bel-subtitle" value={data.subtitle} onChange={(v) => set({ ...data, subtitle: v })} /></Field>
      <Field label="Intro" hint="Rich text">
        <RichText testId="bel-intro" value={data.intro} onChange={(v) => set({ ...data, intro: v })} />
      </Field>
      <Field label="Points">
        <ArrayEditor
          items={data.points}
          onChange={(points) => set({ ...data, points })}
          factory={() => ({ title: "", text: "" })}
          addLabel="Add belief"
          testIdBase="bel"
          renderItem={(item, upd) => (
            <div className="space-y-3">
              <TextInput testId="bel-point-title" value={item.title} placeholder="Title" onChange={(v) => upd({ ...item, title: v })} />
              <RichText testId="bel-point-text" value={item.text} onChange={(v) => upd({ ...item, text: v })} />
            </div>
          )}
        />
      </Field>
    </div>
  );
}

function SalvationForm({ data, set }) {
  return (
    <div className="space-y-4">
      <Field label="Heading"><TextInput testId="salv-heading" value={data.heading} onChange={(v) => set({ ...data, heading: v })} /></Field>
      <Field label="Subtitle"><TextInput testId="salv-subtitle" value={data.subtitle} onChange={(v) => set({ ...data, subtitle: v })} /></Field>
      <Field label="Intro" hint="Rich text">
        <RichText testId="salv-intro" value={data.intro} onChange={(v) => set({ ...data, intro: v })} />
      </Field>
      <Field label="Steps">
        <ArrayEditor
          items={data.steps}
          onChange={(steps) => set({ ...data, steps })}
          factory={() => ({ title: "", verse: "", text: "" })}
          addLabel="Add step"
          testIdBase="salv"
          renderItem={(item, upd) => (
            <div className="space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <TextInput testId="salv-step-title" value={item.title} placeholder="Step title" onChange={(v) => upd({ ...item, title: v })} />
                <TextInput testId="salv-step-verse" value={item.verse} placeholder="Verse reference" onChange={(v) => upd({ ...item, verse: v })} />
              </div>
              <RichText testId="salv-step-text" value={item.text} onChange={(v) => upd({ ...item, text: v })} />
            </div>
          )}
        />
      </Field>
      <Field label="Prayer" hint="Rich text">
        <RichText testId="salv-prayer" value={data.prayer} onChange={(v) => set({ ...data, prayer: v })} />
      </Field>
      <Field label="Call to Action"><TextArea testId="salv-cta" value={data.cta} onChange={(v) => set({ ...data, cta: v })} rows={2} /></Field>
    </div>
  );
}

function FooterForm({ data, set }) {
  return (
    <div className="space-y-4">
      <Field label="Tagline"><TextInput testId="foot-tag" value={data.tagline} onChange={(v) => set({ ...data, tagline: v })} /></Field>
      <Field label="Copyright"><TextInput testId="foot-copy" value={data.copyright} onChange={(v) => set({ ...data, copyright: v })} /></Field>
    </div>
  );
}

function LivestreamForm({ data, set }) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <Toggle testId="live-enabled" value={!!data.enabled} onChange={(v) => set({ ...data, enabled: v })} label={data.enabled ? "Banner enabled" : "Banner off"} />
        <div className="inline-flex bg-white border border-[#E5E0D8] rounded-full overflow-hidden text-sm">
          {["sunday", "always"].map((opt) => (
            <button
              key={opt}
              type="button"
              data-testid={`live-showon-${opt}`}
              onClick={() => set({ ...data, show_on: opt })}
              className={`px-4 py-2 ${data.show_on === opt ? "bg-[#2C4C3B] text-white" : "text-[#1C201E]"}`}
            >
              Show on {opt === "sunday" ? "Sundays" : "any day"}
            </button>
          ))}
        </div>
      </div>
      <Field label="Livestream URL" hint="For manual broadcasts, enter a direct YouTube video URL. It plays inside BBC Connect when the banner is enabled.">
        <TextInput testId="live-url" value={data.url} onChange={(v) => set({ ...data, url: v })} />
      </Field>
      <Field label="Banner Text"><TextInput testId="live-text" value={data.text} onChange={(v) => set({ ...data, text: v })} /></Field>

      <div className="p-4 rounded-xl border border-dashed border-[#D4A373]/70 bg-[#D4A373]/10">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <Toggle testId="live-autodetect" value={!!data.auto_detect} onChange={(v) => set({ ...data, auto_detect: v })} label={data.auto_detect ? "Auto-detect from YouTube ON" : "Auto-detect from YouTube OFF"} />
        </div>
        <Field label="YouTube Channel ID or @handle" hint="When auto-detect is on and this channel is live, the banner shows automatically (banner-enabled toggle can stay off).">
          <TextInput testId="live-channel" value={data.youtube_channel_id} onChange={(v) => set({ ...data, youtube_channel_id: v })} placeholder="UCxxxxxxxxxxxxx or @bbcbarani" />
        </Field>
      </div>
    </div>
  );
}

function GivingForm({ data, set }) {
  return (
    <div className="space-y-4">
      <div className="p-4 rounded-xl border border-dashed border-[#D4A373]/70 bg-[#D4A373]/10">
        <div className="flex flex-wrap items-center gap-3 mb-1">
          <Toggle
            testId="give-hide"
            value={!!data.hide_giving}
            onChange={(v) => set({ ...data, hide_giving: v })}
            label={data.hide_giving ? "Giving Section HIDDEN from public site" : "Giving Section VISIBLE on public site"}
          />
        </div>
        <div className="mt-1 text-xs text-[#8A8F8B]">Temporarily hide the giving section from the homepage and navigation menu.</div>
      </div>
      <Field label="Heading"><TextInput testId="give-heading" value={data.heading} onChange={(v) => set({ ...data, heading: v })} /></Field>
      <Field label="Subtitle"><TextInput testId="give-subtitle" value={data.subtitle} onChange={(v) => set({ ...data, subtitle: v })} /></Field>
      <Field label="Intro" hint="Rich text">
        <RichText testId="give-intro" value={data.intro} onChange={(v) => set({ ...data, intro: v })} />
      </Field>
      <Field label="Verse"><TextArea testId="give-verse" value={data.verse} onChange={(v) => set({ ...data, verse: v })} rows={2} /></Field>
      <Field label="Local Giving Methods">
        <ArrayEditor
          items={data.methods}
          onChange={(methods) => set({ ...data, methods })}
          factory={() => ({ name: "", details: "" })}
          addLabel="Add method"
          testIdBase="give-method"
          renderItem={(item, upd) => (
            <div className="space-y-3">
              <TextInput testId="give-method-name" value={item.name} placeholder="Method (e.g. Bank Transfer)" onChange={(v) => upd({ ...item, name: v })} />
              <TextArea testId="give-method-details" value={item.details} placeholder="Details / account info" onChange={(v) => upd({ ...item, details: v })} rows={3} />
            </div>
          )}
        />
      </Field>
    </div>
  );
}

const FORMS = {
  hero: HeroForm,
  schedule: ScheduleForm,
  location: LocationForm,
  ministries: MinistriesForm,
  beliefs: BeliefsForm,
  salvation: SalvationForm,
  footer: FooterForm,
  livestream: LivestreamForm,
  giving: GivingForm,
};

export default function SectionForm({ section, value, onChange }) {
  const [data, setData] = useState(value || {});
  useEffect(() => setData(value || {}), [value, section]);

  const set = (next) => {
    setData(next);
    onChange(next);
  };

  const Form = FORMS[section];
  if (!Form) return <div className="text-sm text-[#5C6661]">No editor for this section.</div>;
  return <Form data={data} set={set} />;
}
